import { useMemo } from 'react';

/**
 * Centralized placeholder utility hook
 * Memoizes placeholders to prevent unnecessary re-renders
 * 
 * @param {Object} options - Placeholder configuration
 * @param {boolean} options.isSpanish - Language flag
 * @param {string} options.fieldValue - Current field value (for conditional placeholders)
 * @param {string} options.type - Placeholder type (see types below)
 * @param {Object} options.custom - Custom placeholder texts { en: string, es: string }
 * @returns {string} Memoized placeholder string
 */
export const usePlaceholder = ({ isSpanish, fieldValue, type, custom }) => {
  return useMemo(() => {
    // Custom placeholders take precedence
    if (custom) {
      return isSpanish ? custom.es : custom.en;
    }

    // For conditional placeholders, always show Spanish version if Spanish, or English "Enter X" version if English (don't change during typing) This prevents placeholder from changing when fieldValue changes during typing
    const showEnter = !isSpanish;

    switch (type) {
      // Simple examples
      case 'example_0':
        return isSpanish ? 'Ej. 0' : 'e.g. 0';
      case 'example_0_00':
        return isSpanish ? 'Ej. 0,00' : 'e.g. 0,00';
      case 'example_512':
        return isSpanish ? 'Ej. 512' : 'e.g. 512';
      case 'example_916769999':
        return isSpanish ? 'Ej. 916769999' : 'e.g. 916769999';
      case 'example_280112345':
        return isSpanish ? 'Ej. 280112345' : 'e.g. 280112345';
      case 'example_surrey':
        return isSpanish ? 'Ej. Surrey' : 'e.g. Surrey';
      case 'example_1_0':
        return isSpanish ? 'Ej. 1,0' : 'e.g. 1,0';

      // MRN
      case 'mrn':
        return showEnter
          ? 'Enter MRN (e.g. 25ES000441122632A5)'
          : 'Entrada MRN (Ej. 25ES000441122632A5)';

      // Search placeholders
      case 'search_declaration':
        return isSpanish ? 'Buscar declaración...' : 'Search declaration...';
      case 'search_procedure':
        return isSpanish ? 'Buscar procedimiento...' : 'Search procedure...';
      case 'search_generic':
        return isSpanish ? 'Buscar...' : 'Search...';

      // Field-specific placeholders (conditional - show "Enter X" only when empty and English)
      case 'enter_registration_date':
        return showEnter ? 'Enter Registration Date' : 'Entrada Fecha Registro';
      case 'enter_declaration_3':
        return showEnter ? 'Enter Declaration 3' : 'Entrada Declaración 3';
      case 'enter_client_id':
        return showEnter ? 'Enter Client ID' : 'Entrada Cliente ID';
      case 'enter_sender_id':
        return showEnter ? 'Enter Sender ID' : 'Entrada Remitente ID';
      case 'enter_agent_id':
        return showEnter ? 'Enter Agent ID' : 'Entrada Agente ID';
      case 'enter_internal_transport_mode':
        return showEnter ? 'Enter Internal Transport Mode' : 'Entrada Modo Transporte Interior';
      case 'enter_number':
        return showEnter ? 'Enter Number' : 'Entrada Número';
      case 'enter_date':
        return showEnter ? 'Enter Date' : 'Entrada Fecha';
      case 'enter_quantity':
        return showEnter ? 'Enter Quantity' : 'Entrada Cantidad';
      case 'enter_remaining_quantity':
        return showEnter ? 'Enter Remaining Quantity' : 'Entrada Cantidad Restante';
      case 'enter_pieces':
        return showEnter ? 'Enter Pieces' : 'Entrada Piezas';
      case 'enter_internal_external':
        return showEnter ? 'Enter Internal or External' : 'Entrada Es Interno o Externo';
      case 'enter_mark':
        return showEnter ? 'Enter Mark' : 'Entrada Marca';
      case 'enter_net_weight':
        return showEnter ? 'Enter Net Weight' : 'Entrada Masa Neta';
      case 'enter_euro_amount':
        return showEnter ? 'Enter Euro Amount' : 'Entrada Cantidad en Euros';
      case 'enter_currency_amount':
        return showEnter ? 'Enter Currency Amount' : 'Entrada Cantidad en Divisas';
      case 'enter_price':
        return showEnter ? 'Enter Price' : 'Entrada Precio';
      case 'enter_goods_description':
        return showEnter ? 'Enter Goods Description' : 'Entrada Descripción de Mercancía';
      case 'enter_observations':
        return showEnter ? 'Enter Observations (e.g. SOIVRE\\nEFITCAN)' : 'Entrada Observaciones (Ej. SOIVRE\\nEFITCAN)';
      case 'enter_observations_simple':
        return showEnter ? 'Enter Observations' : 'Entrada Observaciones';
      case 'enter_total_packages':
        return showEnter ? 'Enter Total Packages' : 'Entrada Total Bultos';
      case 'enter_border_transport_identity':
        return showEnter ? 'Enter Border Transport Identity' : 'Entrada Identidad Transporte Frontera';
      case 'enter_canary_islands_edi_status':
        return showEnter ? 'Enter Canary Islands EDI Status' : 'Entrada Estado EDI Canarias';
      case 'enter_invoice_date':
        return showEnter ? 'Enter Invoice Date' : 'Entrada Fecha Factura';
      case 'enter_additional_identification':
        return showEnter ? 'Enter Additional Identification' : 'Entrada Identificación Adicional';
      case 'enter_has_containers':
        return showEnter ? 'Enter Has Containers' : 'Entrada Tiene Contenedores';
      case 'enter_edi_status':
        return showEnter ? 'Enter EDI Status' : 'Entrada Estado EDI';
      case 'enter_transaction_nature':
        return showEnter ? 'Enter Transaction Nature' : 'Entrada Naturaleza Transacción';
      case 'enter_invoice':
        return showEnter ? 'Enter Invoice' : 'Entrada Factura';
      case 'enter_type':
        return showEnter ? 'Enter Type' : 'Entrada Tipo';
      case 'enter_nc_code':
        return showEnter ? 'Enter NC Code' : 'Entrada Código NC';
      case 'enter_gross_weight':
        return showEnter ? 'Enter Gross Weight' : 'Entrada Masa Bruta';
      case 'enter_statistical_value':
        return showEnter ? 'Enter Statistical Value' : 'Entrada Valor Estadístico';
      case 'enter_community_national_regime':
        return showEnter ? 'Enter Community/National Regime' : 'Entrada Régimen Comunitario/Nacional';
      case 'example_email':
        return isSpanish ? 'Ej. email@example.com' : 'e.g. email@example.com';

      default:
        return '';
    }
  }, [isSpanish, type, custom]); // Removed fieldValue from deps - placeholder should be stable during typing
};

/**
 * Helper function to create placeholder config object
 * Use this for cleaner code
 */
export const createPlaceholder = (type, fieldValue = null, custom = null) => ({
  type,
  fieldValue,
  custom
});

